'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Clock, CheckCircle, AlertTriangle, XCircle, Star, Layers } from 'lucide-react';
import { usePlacementCatalog } from '@/hooks/usePlacementCatalog';

function AvailabilityBadge({ capacity, filled }: { capacity: number; filled: number }) {
  const remaining = capacity - filled;
  if (remaining === 0) return <span className="availability-sold-out chip"><XCircle size={10} className="mr-1" />Sold Out</span>;
  if (capacity === 1 && remaining === 1) return <span className="availability-available chip"><CheckCircle size={10} className="mr-1" />Available</span>;
  if (remaining <= 2) return <span className="availability-limited chip"><AlertTriangle size={10} className="mr-1" />{remaining} left</span>;
  return <span className="availability-available chip"><CheckCircle size={10} className="mr-1" />{remaining} of {capacity} available</span>;
}

function BookMockup({ position, color }: { position: string; color: string }) {
  const colorMap: Record<string, string> = {
    brand: 'from-brand-700 to-brand-900',
    sky: 'from-sky-700 to-sky-900',
    violet: 'from-violet-700 to-violet-900',
    amber: 'from-amber-700 to-amber-900',
    gradient: 'from-brand-600 via-purple-700 to-brand-900',
  };

  const highlights: Record<string, { label: string; desc: string }> = {
    interior: { label: 'STANDARD PAGE', desc: 'Interior spread' },
    'inside-front': { label: 'INSIDE FRONT', desc: 'Page 2' },
    'inside-back': { label: 'INSIDE BACK', desc: 'Page 11' },
    back: { label: 'BACK COVER', desc: 'Page 12 — Exterior' },
    front: { label: 'FRONT COVER', desc: 'Page 1 — Exterior' },
  };

  const hl = highlights[position] || { label: 'AD PAGE', desc: '' };

  const isPremiumPosition = position === 'back' || position === 'front' || position === 'inside-front' || position === 'inside-back';

  return (
    <div className={`relative rounded-xl bg-gradient-to-br ${colorMap[color] || colorMap.brand} aspect-[8.5/5.5] flex items-center justify-center overflow-hidden border border-white/10`}>
      <div className="absolute inset-0 grid-texture opacity-20" />
      {/* Book spine indicator */}
      <div className="absolute left-0 top-0 bottom-0 w-3 bg-black/30" />
      {isPremiumPosition && (
        <div className="absolute top-2 right-2 text-[9px] font-bold text-white/70 bg-white/10 border border-white/20 rounded px-1.5 py-0.5 tracking-wider">
          PREMIUM
        </div>
      )}
      <div className="relative z-10 text-center px-6">
        <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/15 border border-white/20 mb-2">
          <Layers size={14} className="text-white" />
        </div>
        <p className="text-xs font-bold text-white tracking-widest">{hl.label}</p>
        <p className="text-xs text-white/60 mt-0.5">{hl.desc}</p>
        {/* Decorative content lines */}
        <div className="mt-3 space-y-1 opacity-30">
          {[60, 80, 50, 70].map((w, i) => (
            <div key={`mock-line-${i}`} className="h-1 rounded-full bg-white mx-auto" style={{ width: `${w}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PlacementsContent() {
  const [selectedTerm, setSelectedTerm] = useState<'one' | 'three'>('one');
  const [hoveredPlacement, setHoveredPlacement] = useState<string | null>(null);
  const { placements, source } = usePlacementCatalog();

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-16">
      {/* Header */}
      <div className="mb-10">
        <p className="text-xs font-semibold text-brand-400 uppercase tracking-widest mb-3">Ad Placements</p>
        <h1 className="font-display text-4xl sm:text-5xl font-800 mb-4">
          <span className="text-white">Choose Your </span>
          <span className="text-gradient">Placement</span>
        </h1>
        <p className="text-slate-400 max-w-2xl">
          Five placement positions available per region. Premium positions are exclusive — only 1 per region per issue. Standard inside pages have 9 slots.
        </p>
      </div>

      {/* Term selector */}
      <div className="flex items-center gap-4 mb-10">
        <span className="text-sm text-slate-400">Pricing for:</span>
        <div className="flex p-1 glass-card rounded-xl">
          <button
            onClick={() => setSelectedTerm('one')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${selectedTerm === 'one' ? 'bg-gradient-primary text-white' : 'text-slate-400 hover:text-white'}`}
          >
            One Issue
          </button>
          <button
            onClick={() => setSelectedTerm('three')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-1.5 ${selectedTerm === 'three' ? 'bg-gradient-primary text-white' : 'text-slate-400 hover:text-white'}`}
          >
            <Star size={13} />
            3-Issue Monthly
          </button>
        </div>
        {selectedTerm === 'three' && (
          <span className="text-xs text-green-400 font-medium">Save up to $200/mo per region</span>
        )}
      </div>

      {/* Placement cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-5 mb-14">
        {placements.map((p) => {
          const price = selectedTerm === 'one' ? p.oneIssue : p.threeIssue;
          const isSoldOut = p.defaultFilled >= p.capacity;
          return (
            <div
              key={`placement-card-${p.id}`}
              onMouseEnter={() => setHoveredPlacement(p.id)}
              onMouseLeave={() => setHoveredPlacement(null)}
              className={`glass-card rounded-2xl overflow-hidden border transition-all duration-250 flex flex-col ${
                p.premium ? 'border-brand-600/30' : 'border-white/8'
              } ${hoveredPlacement === p.id ? 'border-brand-500/50 shadow-card-hover -translate-y-1' : ''}`}
            >
              {/* Mockup */}
              <div className="p-4 pb-0">
                <BookMockup position={p.mockupPosition} color={p.color} />
              </div>

              <div className="p-5 flex flex-col flex-1">
                {/* Badge row */}
                <div className="flex items-center gap-2 mb-3">
                  {p.premium && (
                    <span className="text-xs font-semibold text-brand-300 bg-brand-600/15 border border-brand-600/25 px-2 py-0.5 rounded-full">
                      Premium
                    </span>
                  )}
                  <AvailabilityBadge capacity={p.capacity} filled={p.defaultFilled} />
                </div>

                <h3 className="font-display font-700 text-base text-white mb-1">{p.name}</h3>
                <p className="text-xs text-brand-400 font-medium mb-3">{p.position}</p>
                <p className="text-xs text-slate-400 leading-relaxed mb-4 flex-1">{p.description}</p>

                {/* Features */}
                <ul className="space-y-1.5 mb-5">
                  {p.features.map((f) => (
                    <li key={`feature-${p.id}-${f}`} className="flex items-center gap-2 text-xs text-slate-400">
                      <CheckCircle size={11} className="text-green-400 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Price */}
                <div className="mb-4">
                  <p className="font-display font-800 text-2xl text-white tabular-nums">
                    ${price.toLocaleString()}
                    {selectedTerm === 'three' && <span className="text-sm font-normal text-slate-400">/mo</span>}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">per region · {selectedTerm === 'three' ? '3 consecutive issues' : 'single issue'}</p>
                </div>

                {/* CTA */}
                <Link
                  href={`/order-wizard?placement=${p.id}&term=${selectedTerm}`}
                  className={`flex items-center justify-center gap-2 w-full py-2.5 text-sm font-semibold rounded-xl transition-all ${
                    isSoldOut
                      ? 'bg-white/5 text-slate-500 cursor-not-allowed pointer-events-none' :'btn-primary'
                  }`}
                >
                  {isSoldOut ? 'Sold Out' : <>Select Placement <ArrowRight size={14} /></>}
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Comparison table */}
      <div className="glass-card rounded-2xl overflow-hidden border border-white/8">
        <div className="px-6 py-4 border-b border-white/8">
          <h2 className="font-display font-700 text-lg text-white">Placement Comparison</h2>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/8">
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Placement</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Position</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Slots / Region</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">One Issue</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">3-Issue / mo</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Availability</th>
              </tr>
            </thead>
            <tbody>
              {placements.map((p, i) => (
                <tr key={`table-placement-${p.id}`} className={`border-b border-white/5 hover:bg-white/3 transition-colors ${i % 2 === 0 ? '' : 'bg-white/2'}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">{p.name}</span>
                      {p.premium && <span className="chip" style={{ fontSize: '10px' }}>Premium</span>}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-400">{p.position}</td>
                  <td className="px-4 py-4 text-sm text-slate-300 tabular-nums">{p.capacity}</td>
                  <td className="px-4 py-4 text-right">
                    <span className="font-display font-600 text-sm text-white tabular-nums">${p.oneIssue.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="font-display font-600 text-sm text-brand-400 tabular-nums">${p.threeIssue.toLocaleString()}/mo</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <AvailabilityBadge capacity={p.capacity} filled={p.defaultFilled} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Live hold notice */}
      <div className="mt-6 glass-card rounded-xl p-4 border border-amber-500/20 flex items-start gap-3">
        <Clock size={16} className="text-amber-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-400">15-Minute Live Hold on Premium Placements</p>
          <p className="text-xs text-slate-400 mt-0.5">
            When you select a premium placement in the order wizard, it is temporarily reserved for 15 minutes. Complete checkout to convert to a confirmed reservation. Abandoned holds release automatically.
          </p>
        </div>
      </div>
      {source === 'fallback' && (
        <p className="mt-3 text-xs text-slate-500">
          Showing fallback catalog values. Add records in Admin Placements to publish live placement data.
        </p>
      )}
    </div>
  );
}